import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CloudUpload } from 'lucide-react';
import { type BaseSyntheticEvent, type ChangeEvent, useEffect, useState } from 'react';
import {
    type FieldArrayWithId,
    type FieldErrors,
    type UseFieldArrayRemove,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormTrigger,
    type UseFormWatch,
    useFieldArray,
    useForm,
} from 'react-hook-form';
import z from 'zod';
import { Button } from '@/components/Button';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { InvalidRequest } from '@/components/InvalidRequest';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import * as shared from '../tasks.css';
import { AggregationTypeField } from './-fragments/AggregationTypeField';
import { CrossConditionSettings } from './-fragments/CrossConditionSettings';
import { InputFilesField } from './-fragments/InputFilesField';
import * as styles from './-fragments/NewReportingTaskForm.css';
import { OrganizationListView } from './-fragments/OrganizationListView';
import { TaskTypeField } from './-fragments/TaskTypeField';
import {
    AGGREGATION_TYPE_SLOTS,
    type AggregationType,
    buildDefaultValues,
    buildInputFileSlots,
    createTaskSchema,
    DEFAULT_AGGREGATION_TYPE,
    EMPTY_CROSS_CONDITION,
    type InputFile,
    type InputFileSlot,
    resolveInputFiles,
    submitReportingTask,
    useAttributeSettings,
    useReportingTaskFormOptions,
    useSelectedOrganization,
} from './hooks';

/*
 * react-hook-form operates on the schema's INPUT type (before zod applies
 * defaults), which differs from CreateTaskFormData (the OUTPUT) — e.g.
 * `separateByAttr` is optional here because of its `.default('')`. The control /
 * field types must use this to match the actual useForm / useFieldArray instances.
 */
type TaskFormValues = z.input<typeof createTaskSchema>;

/** The react-hook-form controls exposed to the form's presentational sections. */
export type ReportingTaskFormControls = {
    onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
    register: UseFormRegister<TaskFormValues>;
    watch: UseFormWatch<TaskFormValues>;
    setValue: UseFormSetValue<TaskFormValues>;
    trigger: UseFormTrigger<TaskFormValues>;
    formErrors: FieldErrors<TaskFormValues>;
    isFormValid: boolean;
    isFormSubmitting: boolean;
};

export type InputFilesFieldState = {
    slots: InputFileSlot[];
    inputFilesRows: FieldArrayWithId<TaskFormValues, 'inputFiles', 'id'>[];
    setInputFile: (e: ChangeEvent<HTMLInputElement>, index: number, rowFile: InputFile) => Promise<void>;
};

export type CrossConditionsFieldState = {
    crossConditionRows: FieldArrayWithId<TaskFormValues, 'crossConditions', 'id'>[];
    addCrossCondition: () => void;
    updateCrossCondition: (rowIndex: number, colIndex: number, value: string) => void;
    removeCrossConditionRow: UseFieldArrayRemove;
};

export const Route = createFileRoute('/system/_auth/reporting/tasks/new/')({
    component: SystemNewReportingTaskRoute,
    validateSearch: z.object({
        organizationId: uuidSchema.nullish(),
    }),
    errorComponent: () => <InvalidRequest title="タスク新規作成" kind="system" />,
});

// Rails: NewTask.vue
function SystemNewReportingTaskRoute() {
    const navigate = useNavigate();
    const { organizationId } = Route.useSearch();

    const handleTaskCreated = (taskUuid: string) => {
        navigate({
            to: '/system/reporting/tasks/$taskId',
            params: { taskId: taskUuid },
            search: { new: true },
        });
    };

    const [aggregationType, setAggregationType] = useState<AggregationType>(DEFAULT_AGGREGATION_TYPE);

    const showForm = organizationId !== undefined;
    const organization = useSelectedOrganization(organizationId ?? null, showForm);
    const { taskTypes } = useReportingTaskFormOptions(showForm);

    const {
        handleSubmit,
        register,
        watch,
        setValue,
        formState: { errors, isValid, isSubmitting },
        control,
        trigger,
        reset,
    } = useForm({
        defaultValues: buildDefaultValues(organizationId ?? null, DEFAULT_AGGREGATION_TYPE),
        resolver: zodResolver(createTaskSchema),
    });

    /*
     * The form is hoisted to the route and stays mounted while the user switches
     * clients (no remount per selection), so re-seed it whenever the selected
     * client changes to keep organizationId correct and fields fresh. The
     * aggregation type is reset alongside so it stays in sync with the input-file
     * rows.
     */
    useEffect(() => {
        setAggregationType(DEFAULT_AGGREGATION_TYPE);
        reset(buildDefaultValues(organizationId ?? null, DEFAULT_AGGREGATION_TYPE));
    }, [organizationId, reset]);

    const {
        fields: inputFilesRows,
        update: updateInputFileRow,
        replace: replaceInputFileRows,
    } = useFieldArray({ control, name: 'inputFiles' });

    const {
        fields: crossConditionRows,
        append: appendCrossConditionRow,
        remove: removeCrossConditionRow,
        update: updateCrossConditionRow,
    } = useFieldArray({ control, name: 'crossConditions' });

    const inputFileSlots = AGGREGATION_TYPE_SLOTS[aggregationType];
    const attributeSettings = useAttributeSettings(inputFilesRows);

    const changeAggregationType = (next: AggregationType) => {
        setAggregationType(next);
        replaceInputFileRows(buildInputFileSlots(next));
    };

    const setInputFile = async (e: ChangeEvent<HTMLInputElement>, index: number, rowFile: InputFile) => {
        const file = e.target.files?.item(0);
        if (!file) return;
        updateInputFileRow(index, { ...rowFile, file });
    };

    const addCrossCondition = () => {
        appendCrossConditionRow({ values: EMPTY_CROSS_CONDITION });
    };

    const updateCrossCondition = (rowIndex: number, colIndex: number, value: string) => {
        const row = crossConditionRows[rowIndex];
        row.values[colIndex] = value;
        updateCrossConditionRow(rowIndex, row);
    };

    const form: ReportingTaskFormControls = {
        onSubmit: handleSubmit((formValues) =>
            submitReportingTask({
                formValues,
                inputFiles: resolveInputFiles(inputFilesRows, inputFileSlots),
                crossConditions: crossConditionRows,
                onSuccess: handleTaskCreated,
            })
        ),
        register,
        watch,
        setValue,
        trigger,
        formErrors: errors,
        isFormValid: isValid,
        isFormSubmitting: isSubmitting,
    };

    const inputFiles: InputFilesFieldState = {
        slots: inputFileSlots,
        inputFilesRows,
        setInputFile,
    };

    // Whether every required slot for the selected aggregation type has a file
    // (optional slots are excluded).
    const allRequiredFilesSelected = inputFileSlots
        .filter((slot) => slot.required)
        .every((slot) => inputFilesRows.find((row) => row.type === slot.slotType)?.file);

    const crossConditions: CrossConditionsFieldState = {
        crossConditionRows,
        addCrossCondition,
        updateCrossCondition,
        removeCrossConditionRow,
    };

    // Survey tasks (new / overwrite) import into a survey and can't be
    // cross-tabulated, so the cross-condition settings are hidden for them.
    const taskTypeId = watch('taskSurvey.taskTypeId');
    const isSurveyTask = taskTypeId === 'NEW_SURVEY' || taskTypeId === 'OVERWRITE_SURVEY';
    const surveyTypeLabel = taskTypeId === 'NEW_SURVEY' ? '新規' : '上書き';

    const formStatus =
        organizationId && organization.isOrganizationLoading
            ? 'loading'
            : organization.isOrganizationError
              ? 'error'
              : 'ready';

    if (!showForm) {
        return (
            <Page.Root>
                <Page.Header title="タスク新規作成" />

                <Page.Content>
                    <Page.BackLink to="/system/reporting" label="タスク一覧に戻る" />

                    <OrganizationListView
                        onSelect={(organizationId) => navigate({ to: '.', search: { organizationId } })}
                    />
                </Page.Content>
            </Page.Root>
        );
    }

    return (
        <Page.Root>
            <Page.Header title="タスク新規作成" />

            <Page.Content>
                <Page.BackLink to="/system/reporting/tasks/new" label="クライアント選択に戻る" />

                {formStatus === 'loading' && (
                    <Section>
                        <div>クライアントの取得中...</div>
                    </Section>
                )}

                {formStatus === 'error' && (
                    <Section>
                        <Flex direction="column" gap="2">
                            <p>クライアントを取得できませんでした。</p>
                            <p>時間をおいて、画面のリロードをお試しください。</p>
                            <p>エラー: {organization.organizationError?.message}</p>
                        </Flex>
                    </Section>
                )}

                {formStatus === 'ready' && (
                    <Form.Root onSubmit={form.onSubmit}>
                        <Section>
                            <Flex direction="column" gap="5">
                                <div>
                                    <div className={shared.sectionLabel}>クライアント名</div>
                                    {organizationId === null && <div className={styles.orgNameEmpty}>指定なし</div>}
                                    {organization.organization && (
                                        <div className={shared.orgName}>{organization.organization.name}</div>
                                    )}
                                </div>

                                <Form.Field
                                    label="タスク名"
                                    required
                                    control={(props) => <Input {...props} {...form.register('name')} type="text" />}
                                    error={form.formErrors.name?.message}
                                />

                                <TaskTypeField form={form} taskTypes={taskTypes} organizationId={organizationId} />

                                <AggregationTypeField value={aggregationType} onChange={changeAggregationType} />

                                <InputFilesField inputFiles={inputFiles} form={form} />

                                <hr className={shared.divider} />

                                <span className={styles.taskSettingsLabel}>タスク設定</span>

                                <hr className={shared.divider} />

                                <CrossConditionSettings
                                    isSurveyTask={isSurveyTask}
                                    surveyTypeLabel={surveyTypeLabel}
                                    attributeSettings={attributeSettings}
                                    form={form}
                                    crossConditions={crossConditions}
                                />

                                {(isSurveyTask || attributeSettings.length > 0) && (
                                    <>
                                        <hr className={shared.divider} />

                                        <Flex justify="end">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={
                                                    form.isFormSubmitting ||
                                                    !form.isFormValid ||
                                                    !allRequiredFilesSelected
                                                }
                                            >
                                                <Icon icon={CloudUpload} />
                                                {form.isFormSubmitting ? 'アップロード中...' : 'アップロード'}
                                            </Button>
                                        </Flex>
                                    </>
                                )}
                            </Flex>
                        </Section>
                    </Form.Root>
                )}
            </Page.Content>
        </Page.Root>
    );
}
